import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, Users, Ticket, Menu, Plus, UserPlus, Coins, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { isPro as checkIsPro } from '../../utils/planPermissions';
import { canOpenPath } from '../../utils/modulePermissions';
import { useClientRequestsInfo } from '../../api/clientRequestsApi';

// Barra de navegación inferior fija — SOLO móvil (md:hidden).
// Pensada para el dueño del negocio (no para admins de Lealify, que siguen con
// el drawer). El botón "Más" abre el menú lateral existente (toggleSidebar), así
// que NO se pierde el acceso a ninguna sección. El FAB central abre una hoja con
// las acciones del día (registrar cliente / cargar puntos / canjear).

interface BottomNavProps {
  toggleSidebar: () => void;
}

interface TabDef {
  path: string;
  icon: React.ElementType;
  label: string;
}

const TABS_LEFT: TabDef[] = [
  { path: '/dashboard', icon: Home, label: 'Inicio' },
  { path: '/dashboard/clients', icon: Users, label: 'Clientes' },
];
const TABS_RIGHT: TabDef[] = [
  { path: '/dashboard/redeem-rewards', icon: Ticket, label: 'Canjear' },
];

const BottomNav: React.FC<BottomNavProps> = ({ toggleSidebar }) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { currentUser } = useAuth();
  const [sheetOpen, setSheetOpen] = useState(false);
  // «Mis peticiones» vive en el menú (detrás de «Más»): un punto avisa que esperamos un dato (11-oct-2026).
  const { waiting: requestsWaiting } = useClientRequestsInfo();

  // Los admins de Lealify usan el panel de admin (otra navegación) → sin bottom bar.
  if (currentUser?.role === 'admin') return null;

  const hasProAccess = checkIsPro(currentUser?.subscription);

  // Gate por módulo (F3): mismo mapa central que el sidebar y el guard del Layout.
  const canSee = (path: string) => canOpenPath(currentUser, path);

  const go = (path: string) => {
    setSheetOpen(false);
    navigate(path);
  };

  const isActive = (path: string) =>
    path === '/dashboard' ? pathname === '/dashboard' : pathname.startsWith(path);

  // Acciones del FAB (cargar puntos solo si el plan lo permite).
  const actions = [
    { path: '/dashboard/register-client', icon: UserPlus, label: 'Registrar cliente' },
    ...(hasProAccess
      ? [{ path: '/dashboard/recharge-points', icon: Coins, label: 'Cargar puntos' }]
      : []),
    { path: '/dashboard/redeem-rewards', icon: Ticket, label: 'Canjear recompensa' },
  ].filter((a) => canSee(a.path));

  const TabButton: React.FC<{ tab: TabDef }> = ({ tab }) => {
    const active = isActive(tab.path);
    return (
      <button
        onClick={() => go(tab.path)}
        className="flex flex-col items-center justify-center gap-0.5 flex-1 h-full"
        aria-label={tab.label}
      >
        <tab.icon className={`h-5 w-5 ${active ? 'text-accent' : 'text-content-3'}`} />
        <span className={`text-[10px] font-medium ${active ? 'text-accent' : 'text-content-3'}`}>
          {tab.label}
        </span>
      </button>
    );
  };

  return (
    <>
      {/* Hoja inferior de acciones (FAB) */}
      {sheetOpen && (
        <div className="md:hidden fixed inset-0 z-40">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setSheetOpen(false)}
            aria-hidden="true"
          />
          <div className="absolute inset-x-0 bottom-0 bg-surface rounded-t-2xl shadow-xl pb-[calc(1rem+env(safe-area-inset-bottom))] pt-2 animate-fade-in-up">
            <div className="mx-auto mt-1 mb-3 h-1 w-10 rounded-full bg-content-3/30" />
            <div className="flex items-center justify-between px-5 pb-2">
              <p className="text-sm font-semibold text-content">¿Qué quieres hacer?</p>
              <button
                onClick={() => setSheetOpen(false)}
                className="p-1.5 rounded-full text-content-3 hover:bg-surface-2"
                aria-label="Cerrar"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="px-3 pb-2">
              {actions.map((a) => (
                <button
                  key={a.path}
                  onClick={() => go(a.path)}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-surface-2 transition-colors"
                >
                  <span className="h-9 w-9 rounded-lg bg-accent/15 flex items-center justify-center">
                    <a.icon className="h-5 w-5 text-accent" />
                  </span>
                  <span className="text-sm font-medium text-content">{a.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Barra inferior */}
      <nav className="md:hidden fixed inset-x-0 bottom-0 z-30 bg-surface border-t border-line pb-[env(safe-area-inset-bottom)]">
        <div className="relative flex h-14 items-stretch">
          {TABS_LEFT.filter((t) => canSee(t.path)).map((t) => (
            <TabButton key={t.path} tab={t} />
          ))}

          {/* Hueco central para el FAB */}
          <div className="flex-1" aria-hidden="true" />

          {TABS_RIGHT.filter((t) => canSee(t.path)).map((t) => (
            <TabButton key={t.path} tab={t} />
          ))}

          <button
            onClick={toggleSidebar}
            className="flex flex-col items-center justify-center gap-0.5 flex-1 h-full"
            aria-label={requestsWaiting > 0 ? `Más opciones (${requestsWaiting} petición(es) esperan tu respuesta)` : 'Más opciones'}
          >
            <span className="relative">
              <Menu className="h-5 w-5 text-content-3" />
              {requestsWaiting > 0 && (
                <span className="absolute -right-1.5 -top-1 h-2.5 w-2.5 rounded-full bg-amber-500 ring-2 ring-surface" aria-hidden="true" data-testid="peticiones-punto-mas" />
              )}
            </span>
            <span className="text-[10px] font-medium text-content-3">Más</span>
          </button>

          {/* FAB central elevado */}
          <button
            onClick={() => setSheetOpen(true)}
            className="absolute left-1/2 -translate-x-1/2 -top-5 h-14 w-14 rounded-full bg-accent-solid text-white shadow-lg flex items-center justify-center active:scale-95 transition-transform ring-4 ring-surface"
            aria-label="Acciones rápidas"
          >
            <Plus className="h-7 w-7" />
          </button>
        </div>
      </nav>
    </>
  );
};

export default BottomNav;
